import { useRef, type CSSProperties, type ReactNode } from 'react';
import { SoundMixExperienceSection } from '../../../components/landing/sleep/SoundMixExperienceSection';
import { onClickPageButtonTracking, usePageTracking, useScrollTracking } from '../../../hooks/pageTracking';
import { LandingButtonClickActionType, UserActionEnum } from '../../../types/customLogger';
import { userAnalytics } from '../../../utils/analytics/userAnalytics';
import soundMixProfile1 from '../../../assets/images/landing/sleep/soundMix/profile1.png';
import soundMixProfile2 from '../../../assets/images/landing/sleep/soundMix/profile2.png';
import soundMixProfile3 from '../../../assets/images/landing/sleep/soundMix/profile3.png';
import soundMixCoverTrain from '../../../assets/images/landing/sleep/soundMix/비가오는기차.png';
import soundMixCoverForest from '../../../assets/images/landing/sleep/soundMix/비가오는숲.png';
import soundMixCoverHut from '../../../assets/images/landing/sleep/soundMix/바람부는오두막모닥불.png';
import mixCardPlayIcon from '../../../assets/images/landing/sleep/mix-card-play.svg';
import mixCardThumbsUpIcon from '../../../assets/images/landing/sleep/mix-card-thumbs-up.svg';

const PAGE_NAME = 'lp_sleep_sound_mix';
const CTA_LABEL = '내 취향 믹스 만들러 가기';
/** TODO: 믹스 랜딩 전용 딥링크로 교체 가능 */
const MIX_DEEP_LINK = 'https://limh6.app.link/sIJ1YMbVX2b';

/** 후기 행: 뷰포트 줄 때 아바타·간격·말풍선·꼬리가 같은 축으로 스케일 */
const QUOTE_SECTION_VARS: CSSProperties = {
  ['--qa-avatar' as string]: 'clamp(52px, 18vw, 208px)',
  ['--qa-gap' as string]: 'clamp(12px, 5.2vw, 70px)',
  ['--qa-tail' as string]: 'clamp(34px, 5.4vw, 62px)',
  ['--qa-tail-l' as string]: 'calc(var(--qa-tail) * -35 / 62)',
  ['--qa-tail-h' as string]: 'calc(var(--qa-tail) * 56 / 62)',
  ['--qa-tail-join' as string]: 'calc(var(--qa-tail) * 27 / 62)',
  ['--qa-text' as string]: 'clamp(14px, 3.2vw, 40px)',
  ['--qa-bubble-px' as string]: 'clamp(14px, 2.5vw, 40px)',
  ['--qa-bubble-py' as string]: 'clamp(8px, 1.6vw, 16px)',
};

const SOUND_MIX_REVIEW_AVATAR_SRC: Record<number, string> = {
  1: soundMixProfile1,
  2: soundMixProfile2,
  3: soundMixProfile3,
};

function SpeechBubble({ children, align }: { children: ReactNode; align: 'left' | 'right' }) {
  const tailStyle =
    align === 'left'
      ? ({ left: 'var(--qa-tail-l)', width: 'var(--qa-tail)', height: 'var(--qa-tail-h)' } as const)
      : ({
          left: 'calc(100% - var(--qa-tail-join))',
          width: 'var(--qa-tail)',
          height: 'var(--qa-tail-h)',
        } as const);

  const tailPath = align === 'left' ? 'M62 0L0 20.6847L62 56V0Z' : 'M0 0L62 20.6847L0 56Z';

  const tail = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 62 56"
      fill="none"
      className="pointer-events-none absolute bottom-1.5 z-0"
      style={tailStyle}
      aria-hidden
    >
      <path d={tailPath} fill="#444444" />
    </svg>
  );

  const bubbleBody = (
    <div
      className="relative z-10 inline-flex max-w-full min-w-0 flex-col rounded-[10px] bg-[#444] font-normal leading-[1.4] text-white [&_p]:m-0 [&_p]:min-w-0 [&_p]:max-w-full [&_p]:truncate [&_p+p]:mt-1"
      style={{
        paddingLeft: 'var(--qa-bubble-px)',
        paddingRight: 'var(--qa-bubble-px)',
        paddingTop: 'var(--qa-bubble-py)',
        paddingBottom: 'var(--qa-bubble-py)',
        fontSize: 'var(--qa-text)',
      }}
    >
      {tail}
      <div className="relative z-10 flex min-w-0 flex-col">{children}</div>
    </div>
  );

  const shell = <div className="relative inline-flex max-w-full min-w-0 flex-col">{bubbleBody}</div>;

  if (align === 'right') {
    return <div className="flex w-full min-w-0 justify-end">{shell}</div>;
  }
  return shell;
}

function AvatarCircle({ profile }: { profile: number }) {
  const src = SOUND_MIX_REVIEW_AVATAR_SRC[profile] ?? soundMixProfile1;
  return (
    <div
      className="flex aspect-square w-[var(--qa-avatar,208px)] max-w-[var(--qa-avatar,208px)] shrink-0 items-center justify-center rounded-full ring-1 ring-white/10"
      aria-hidden
    >
      <img src={src} alt="" className="h-full w-full object-cover" />
    </div>
  );
}

/** 재생 원·아이콘 공통: 기본 60px / 32px, 창이 줄면 max-* 구간마다 고정 단계. (Tailwind 3.3에는 `size-*` 없음 → h/w 명시) */
const MIX_CARD_HIT_CIRCLE =
  'h-[60px] w-[60px] max-lg:h-[52px] max-lg:w-[52px] max-md:!h-[44px] max-md:!w-[44px] max-sm:!h-[36px] max-sm:!w-[36px]';
const MIX_CARD_HIT_ICON =
  'h-[32px] w-[32px] object-contain max-lg:h-[28px] max-lg:w-[28px] max-md:!h-[24px] max-md:!w-[24px] max-sm:!h-[20px] max-sm:!w-[20px]';

  /** `title`과 `src/assets/images/landing/sleep/soundMix/` 아래 파일명(공백 제거) 대응 */
const SOUND_MIX_CARD_COVER: Record<string, string> = {
  '비가 오는 기차': soundMixCoverTrain,
  '비가 오는 숲': soundMixCoverForest,
  '바람부는 오두막 모닥불': soundMixCoverHut,
};


function MixCard({
  title,
  count,
  featured,
  gradientClass,
  compactTitle,
}: {
  title: string;
  count: string;
  featured?: boolean;
  gradientClass: string;
  /** 좌측 등 좁은 카드용 더 작은 제목 상한(24px) */
  compactTitle?: boolean;
}) {
  const rounded = featured ? 'rounded-[20px]' : 'rounded-[17.647px]';
  const shadow = featured ? 'shadow-[0_0_50px_rgba(0,0,0,0.7)]' : '';

  const titleClass = featured
    ? 'text-[clamp(16px,3.4vw,40px)]'
    : compactTitle
      ? 'text-[clamp(11px,2.2vw,24px)]'
      : 'text-[clamp(12px,2.6vw,35.29px)]';

  const outerFrame = featured
    ? 'flex aspect-[357/472] w-full max-h-[472px] max-w-[357px] min-h-0 flex-col overflow-hidden'
    : 'flex aspect-[315/416] w-full max-h-[416px] max-w-[315px] min-h-0 flex-col overflow-hidden';

  return (
    <div className={`${rounded} ${shadow} mx-auto bg-[#989898] ${outerFrame}`}>
      <div className={`relative min-h-0 w-full flex-[237_1_0] overflow-hidden ${gradientClass}`}>
        {SOUND_MIX_CARD_COVER[title] ? (
          <img src={SOUND_MIX_CARD_COVER[title]} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : null}
      </div>
      <div className="relative flex min-h-0 w-full flex-[179_0_0] flex-col justify-between overflow-hidden bg-[#323232] p-[clamp(10px,3.8vw,30px)]">
        <p className={`shrink-0 font-semibold leading-tight text-white ${titleClass}`}>{title}</p>
        <div className="flex shrink-0 items-end gap-[clamp(6px,1.5vw,12px)]">
          <div
            className={`flex shrink-0 items-center justify-center rounded-full bg-white/20 ${MIX_CARD_HIT_CIRCLE}`}
          >
            <img
              src={mixCardPlayIcon}
              alt=""
              className={`ml-0.5 ${MIX_CARD_HIT_ICON}`}
              width={32}
              height={32}
            />
          </div>
          <div className="ml-auto flex items-center gap-[clamp(6px,1.8vw,14px)]">
            <span
              className={`text-white ${
                featured ? 'text-[clamp(14px,3.2vw,30px)]' : 'text-[clamp(12px,2.8vw,26px)]'
              }`}
            >
              {count}
            </span>
            <img
              src={mixCardThumbsUpIcon}
              alt=""
              className={`shrink-0 ${MIX_CARD_HIT_ICON}`}
              width={32}
              height={32}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


export default function LpSoundMix() {
  const scrollRef = useRef<HTMLDivElement>(null);

  usePageTracking(PAGE_NAME);
  useScrollTracking(scrollRef, PAGE_NAME);

  const onClickCta = () => {
    onClickPageButtonTracking(PAGE_NAME, CTA_LABEL);
    const action = `${UserActionEnum.LANDING_BUTTON_CLICK}_${CTA_LABEL}` as LandingButtonClickActionType;
    userAnalytics(action, PAGE_NAME);
    window.location.href = MIX_DEEP_LINK;
  };

  return (
    <div ref={scrollRef} className="h-[100dvh] overflow-y-auto bg-[#101010]">
      <div className="mx-auto w-full max-w-[1000px] px-4 pb-16 pt-10 md:px-8 md:pt-12">
        <p className="text-center text-[clamp(18px,3.6vw,40px)] font-medium leading-[1.4] text-white">직접 만드는 ASMR 수면음악</p>

        <h1 className="mt-6 text-center text-[clamp(28px,7vw,80px)] font-semibold leading-[1.4] text-white">
          사운드를 눌러
          <br />
          <span className='bg-[#324BC6] px-2'>직접 믹스해</span> 보세요
        </h1>

        <SoundMixExperienceSection />

        <h2 className="mx-auto mt-16 max-w-[640px] text-center text-[clamp(22px,5.2vw,70px)] font-semibold leading-[1.4] text-white md:mt-24">
        이렇게 만든 사운드,
        <br />
        그냥 버리기엔
        <br />
        아깝지 않나요?
      </h2>
      <div
        className="mx-auto mt-12 flex w-full max-w-[960px] items-center justify-center py-6 md:mt-16 md:py-10"
        role="group"
        aria-label="믹스 예시 카드"
      >
        <div className="z-[1] flex max-h-[416px] w-full min-w-0 max-w-[315px] shrink flex-1 origin-right translate-x-4">
          <MixCard title="비가 오는 기차" count="187" gradientClass="bg-gradient-to-br from-slate-500 to-slate-800" />
        </div>
        <div className="relative z-[3] flex w-full min-w-0 max-w-[357px] max-h-[472px] shrink grow-[1.133] basis-0 justify-center -mx-[min(10vw,2.75rem)] md:-mx-12">
          <MixCard
            featured
            title="비가 오는 숲"
            count="256"
            gradientClass="bg-gradient-to-b from-slate-600 via-slate-800 to-slate-900"
          />
        </div>
        <div className="z-[1] flex max-h-[416px] w-full min-w-0 max-w-[315px] shrink flex-1 origin-left -translate-x-4">
          <MixCard
            title="바람부는 오두막 모닥불"
            count="63"
            compactTitle
            gradientClass="bg-gradient-to-br from-amber-900/80 to-stone-900"
          />
        </div>
      </div>
        <p className="mx-auto mt-16 max-w-[640px] text-center text-[clamp(18px,4.2vw,50px)] font-normal leading-[1.4] text-white md:mt-24">
          사운드필에서는
          <br />
          직접 만든 믹스를 저장하고,
        </p>

        <p className="mx-auto mt-6 max-w-[640px] text-center text-[clamp(20px,4.5vw,60px)] font-semibold leading-[1.4] text-white">
          <span className="text-[#fbb12c]">훨씬 많은 사운드</span>를
          <br />
          믹싱할 수 있어요!
        </p>

        <button
          type="button"
          onClick={onClickCta}
          className="mx-auto mt-10 flex h-[clamp(72px,18vw,201px)] w-full max-w-[900px] items-center justify-center rounded-[13px] bg-[#4f67f8] px-4 text-[clamp(20px,4.5vw,52px)] font-semibold leading-[1.3] text-white transition hover:brightness-110 active:brightness-95 md:mt-14"
        >
          내 취향 믹스 만들러 가기 →
        </button>

        <div className="mx-auto mt-12 max-w-[900px] px-4 text-center md:mt-16">
          <p className="text-[clamp(18px,4vw,40px)] font-normal leading-[1.4] text-white">
            이미 1만 명의 사용자가
            <br />
            자신만의 사운드 레시피를 만들었습니다.
          </p>
          <p className="mt-4 text-[clamp(22px,5vw,60px)] font-semibold leading-[1.4] text-white md:mt-6">
            당신의 시그니처 믹스는 무엇인가요?
          </p>
        </div>

        <section
          className="relative mx-auto mt-12 max-w-[900px] md:mt-16"
          aria-label="사용자 후기"
          style={QUOTE_SECTION_VARS}
        >
          <div className="flex flex-col items-center gap-10 md:gap-12">
            <div className="flex w-full min-w-0 max-w-full translate-x-1 flex-row items-center gap-[var(--qa-gap)] sm:translate-x-2">
              <AvatarCircle profile={1 as number} />
              <div className="min-h-0 min-w-0 flex-1 basis-0">
                <SpeechBubble align="left">
                  <p>마음을 편하게 해주는</p>
                  <p>일상적인 소리가 좋아</p>
                </SpeechBubble>
              </div>
            </div>

            <div className="flex w-full min-w-0 max-w-full -translate-x-1 flex-row-reverse items-center gap-[var(--qa-gap)] sm:-translate-x-2">
              <AvatarCircle profile={2 as number} />
              <div className="min-h-0 min-w-0 flex-1 basis-0">
                <SpeechBubble align="right">
                  <p>자연의 소리를 듣고</p>
                  <p>있으면 잠이 잘 와</p>
                </SpeechBubble>
              </div>
            </div>

            <div className="flex w-full min-w-0 max-w-full translate-x-1 flex-row items-center gap-[var(--qa-gap)] sm:translate-x-2">
              <AvatarCircle profile={3 as number} />
              <div className="min-h-0 min-w-0 flex-1 basis-0">
                <SpeechBubble align="left">
                  <p>나를 새로운 세계로</p>
                  <p>데려다주는 공간 사운드</p>
                </SpeechBubble>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
